import { signal } from '@preact/signals';

export type ConnectionStatus = 'idle' | 'connecting' | 'live' | 'stale' | 'offline';

export const connectionStatus = signal<ConnectionStatus>('idle');

/**
 * Timestamp of the most recent waypoint we received. Drives the "Updated Ns
 * ago" copy and the stale-signal transitions.
 */
export const lastWaypointAt = signal<number | null>(null);

/**
 * Thresholds for the stale-signal escalation. After 60s of silence the pill
 * goes amber ("Signal weak"); after 5 minutes it goes red ("Offline").
 * The traveller in a tunnel or elevator typically reappears inside that window,
 * so we deliberately don't alarm the watcher inside the first minute.
 */
export const STALE_AFTER_MS = 60_000;
export const OFFLINE_AFTER_MS = 5 * 60_000;

let staleTimeout: ReturnType<typeof setTimeout> | null = null;
let offlineTimeout: ReturnType<typeof setTimeout> | null = null;

export function markLive(): void {
  connectionStatus.value = 'live';
  resetStaleTimers();
}

export function markStaleTimerOnWaypoint(): void {
  lastWaypointAt.value = Date.now();
  // Only reset the timers if the socket itself is live — receiving a waypoint
  // from a stale-detection perspective shouldn't override a socket disconnect.
  if (connectionStatus.value === 'live' || connectionStatus.value === 'stale') {
    connectionStatus.value = 'live';
    resetStaleTimers();
  }
}

export function markDisconnected(): void {
  connectionStatus.value = 'offline';
  clearStaleTimers();
}

function resetStaleTimers(): void {
  clearStaleTimers();
  staleTimeout = setTimeout(() => {
    if (connectionStatus.value === 'live') connectionStatus.value = 'stale';
  }, STALE_AFTER_MS);
  offlineTimeout = setTimeout(() => {
    if (connectionStatus.value === 'live' || connectionStatus.value === 'stale') {
      connectionStatus.value = 'offline';
    }
  }, OFFLINE_AFTER_MS);
}

function clearStaleTimers(): void {
  if (staleTimeout) clearTimeout(staleTimeout);
  if (offlineTimeout) clearTimeout(offlineTimeout);
  staleTimeout = null;
  offlineTimeout = null;
}
